import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { LogIn, UserPlus, Mail, Lock, AlertCircle, Loader2, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * AuthModal — shared Login / Register modal with Emergent Google Sign-In.
 * Toggle `open` from anywhere (nav, footer, resource card CTA) via the
 * `useAuthModal` helper below.
 */
export function AuthModal({ open, onClose, initialTab = "login", onSuccess }) {
  const [tab, setTab] = useState(initialTab);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const { login, register, startGoogleLogin } = useAuth();

  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setError(null);
      setBusy(false);
    }
  }, [open, initialTab]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !password || (tab === "register" && !name)) {
      setError("Please fill in every field.");
      return;
    }
    setBusy(true);
    const result =
      tab === "login"
        ? await login({ email, password })
        : await register({ name, email, password });
    setBusy(false);
    if (result.ok) {
      onSuccess?.(result.user);
      onClose?.();
      setName("");
      setEmail("");
      setPassword("");
    } else {
      setError(result.error || "Something went wrong.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose?.() : null)}>
      <DialogContent
        data-testid="auth-modal"
        className="max-w-md bg-[#0B1120] border border-white/10 text-white p-0 overflow-hidden"
      >
        <div className="p-6 sm:p-8">
          <DialogHeader className="space-y-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37]">
                {tab === "login" ? <LogIn className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                OGO Community
              </div>
            </div>
            <DialogTitle className="font-serif-display text-2xl leading-tight text-white">
              {tab === "login" ? "Welcome back." : "Join the community."}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm leading-relaxed">
              {tab === "login"
                ? "Sign in to unlock 1-click downloads and your personal library."
                : "Create a free account for instant access to every current and future resource — no forms, no re-entering your email."}
            </DialogDescription>
          </DialogHeader>

          {/* Tab switcher */}
          <div className="mt-6 grid grid-cols-2 border border-white/10 bg-[#142339] text-xs uppercase tracking-[0.22em]">
            {["login", "register"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTab(t);
                  setError(null);
                }}
                data-testid={`auth-tab-${t}`}
                className={`py-3 font-bold transition-colors ${
                  tab === t
                    ? "bg-[#D4AF37] text-[#142339]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {t === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          {/* Google button */}
          <button
            type="button"
            onClick={() => startGoogleLogin("/community")}
            data-testid="auth-google-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-3 border border-white/20 bg-white text-[#142339] px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold hover:bg-slate-100 transition-colors"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-slate-500">
            <div className="flex-1 h-px bg-white/10" />
            <span>or with email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {tab === "register" && (
              <label className="block">
                <span className="sr-only">Name</span>
                <div className="flex items-center gap-2 border border-white/15 bg-[#142339] px-3 focus-within:border-[#D4AF37]">
                  <User className="w-4 h-4 text-slate-500" aria-hidden="true" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    autoComplete="name"
                    className="flex-1 bg-transparent py-3 text-sm text-white placeholder-slate-500 focus:outline-none"
                    data-testid="auth-input-name"
                  />
                </div>
              </label>
            )}
            <label className="block">
              <span className="sr-only">Email</span>
              <div className="flex items-center gap-2 border border-white/15 bg-[#142339] px-3 focus-within:border-[#D4AF37]">
                <Mail className="w-4 h-4 text-slate-500" aria-hidden="true" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="flex-1 bg-transparent py-3 text-sm text-white placeholder-slate-500 focus:outline-none"
                  data-testid="auth-input-email"
                />
              </div>
            </label>
            <label className="block">
              <span className="sr-only">Password</span>
              <div className="flex items-center gap-2 border border-white/15 bg-[#142339] px-3 focus-within:border-[#D4AF37]">
                <Lock className="w-4 h-4 text-slate-500" aria-hidden="true" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={tab === "register" ? "Create a password (min 8 chars)" : "Your password"}
                  autoComplete={tab === "register" ? "new-password" : "current-password"}
                  className="flex-1 bg-transparent py-3 text-sm text-white placeholder-slate-500 focus:outline-none"
                  data-testid="auth-input-password"
                />
              </div>
            </label>

            {error && (
              <div
                data-testid="auth-error"
                className="flex items-start gap-2 border border-red-500/30 bg-red-500/10 text-red-200 text-xs px-3 py-2"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Please wait…
                </>
              ) : tab === "login" ? (
                "Sign In"
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <p className="mt-5 text-[10px] text-slate-500 text-center leading-relaxed">
            By continuing you agree to OGO&apos;s Terms &amp; Privacy Policy.
            We never share your email.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.4 2.8 30 .5 24 .5 14.6.5 6.4 6 2.6 14l7.9 6.1C12.4 13.9 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.6c-.6 3-2.3 5.6-4.9 7.3l7.6 5.9c4.5-4.2 7.2-10.3 7.2-17.7z" />
      <path fill="#FBBC05" d="M10.5 28.6c-.6-1.7-.9-3.5-.9-5.6s.3-3.9.9-5.6L2.6 11.4C.9 14.8 0 18.7 0 23s.9 8.2 2.6 11.6l7.9-6z" />
      <path fill="#34A853" d="M24 47.5c6 0 11.4-2 15.2-5.4l-7.6-5.9c-2.1 1.4-4.8 2.3-7.6 2.3-6.3 0-11.6-4.4-13.5-10.5l-7.9 6C6.4 42 14.6 47.5 24 47.5z" />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}
